import React, { Fragment, useMemo } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { IconButton, InfoPlate } from "fe-shared/src/components";
import { EditIcon } from "fe-shared/src/components/Icons";
import { MaterialGroupByRegYear } from "fe-shared/src/types/domain";
import {
  TransactionTraversedMaterial,
  Transfer,
} from "fe-shared-transactions/src/types";
import { formatTransactionNumber } from "fe-shared-transactions/src/helpers/format";
import getMaterialGroupsByRegYear from "fe-shared-transactions/src/helpers/getMaterialGroupsByRegYear";

import { ActionType } from "../../../types/inventoryAdjustments";
import InventoryAdjustmentDetailsActionTransactionContentRow from "./InventoryAdjustmentDetailsActionTransactionContentRow";

import styles from "../InventoryAdjustmentsDetails.module.scss";

interface Props {
  type: ActionType;
  action: Transfer;
  isEditable?: boolean;
  onEdit: () => void;
}

const InventoryAdjustmentDetailsActionTransaction = ({
  action,
  type,
  isEditable = false,
  onEdit,
}: Props) => {
  const { t } = useTranslation();
  const { transferNumber, materials, traversedMaterials } = action;
  const { units, materialCategoryName } = materials[0];
  const isAddition = type === "addition";
  const showRegYear = traversedMaterials && traversedMaterials?.length > 0;
  const contentTitle = t(
    `inventoryAdjustment.details.categories.${materialCategoryName}`,
    {
      unit: units.unit,
    }
  );

  const traversedMaterialsByRegYear = useMemo(
    () => getMaterialGroupsByRegYear(traversedMaterials),
    [traversedMaterials]
  );

  return (
    <section>
      <div
        className={classnames(
          styles.locationSectionDataContainer,
          styles.actionHeader
        )}
      >
        <div className={styles.locationSectionHeader}>
          <h2>{t(`inventoryAdjustment.details.actions.${type}`)}</h2>
        </div>

        <InfoPlate variant="black" className={styles.statusInfoPlate}>
          {formatTransactionNumber(transferNumber)}
        </InfoPlate>
        {isEditable && (
          <IconButton
            hasTooltip
            id={`edit-inventory-adjustment-button-${transferNumber}`}
            aria-label={t("inventoryAdjustment.actions.Edit.title")}
            className={styles.editButton}
            onClick={() => {
              onEdit();
            }}
          >
            <EditIcon fillColor={null} />
          </IconButton>
        )}
      </div>
      {showRegYear ? (
        traversedMaterialsByRegYear.map(
          (
            groupsByRegYear: MaterialGroupByRegYear<
              TransactionTraversedMaterial
            >
          ) => (
            <Fragment key={`${groupsByRegYear.regYear}-group-reg-year`}>
              <p className={styles.regYearTitle}>
                {groupsByRegYear.regYear
                  ? t("transfers.details.regYear", {
                      regYear: groupsByRegYear.regYear,
                    })
                  : t("transfers.details.unidentifiedRegYear")}
              </p>

              <InventoryAdjustmentDetailsActionTransactionContentRow
                title={contentTitle}
                isAddition={isAddition}
                materials={groupsByRegYear.materialGroups[0].items}
              />
            </Fragment>
          )
        )
      ) : (
        <InventoryAdjustmentDetailsActionTransactionContentRow
          isAddition={isAddition}
          materials={materials}
          title={contentTitle}
        />
      )}
    </section>
  );
};

export default InventoryAdjustmentDetailsActionTransaction;
